import React from "react";
import { Helmet } from 'react-helmet-async';

export default function DPASummary() {
  return (
    <div className="min-h-screen py-12 px-6 lg:px-24">
      <Helmet>
        <title>Data Processing Addendum (DPA) Summary — Andlegur</title>
        <meta name="description" content="Summary of Andlegur's Data Processing Addendum (DPA) outlining our data processing practices and GDPR compliance commitments." />
        <link rel="canonical" href="https://andlegur.com/dpa-summary" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="max-w-4xl mx-auto shadow-xl rounded-2xl p-10">
        <h1 className="text-3xl font-bold text-indigo-200 mb-6">
          Data Processing Addendum (DPA) – Summary
        </h1>
        <p className="text-sm text-indigo-300 mb-4">
          Last updated: <span className="font-medium text-indigo-200">January 9, 2026</span>
        </p>
        <p className="text-indigo-100 mb-8">
          This Data Processing Addendum ("DPA") supplements the Andlegur Terms
          of Service and Privacy Policy. It outlines how we handle the personal
          data of users in compliance with global data protection laws,
          including the General Data Protection Regulation (GDPR), the
          California Consumer Privacy Act (CCPA), and other applicable
          legislation.
        </p>

        <div className="bg-indigo-950/40 border border-indigo-800/50 rounded-lg p-6 mb-8">
          <p className="text-indigo-100">
            <strong className="text-indigo-50">Important Notice:</strong> This DPA does not alter or supersede the terms regarding refunds, purchases, user responsibilities, limitations of liability, or indemnification as set forth in the Terms of Service and Privacy Policy. All purchases remain final and non-refundable, and you remain fully responsible for your use of the Services.
          </p>
        </div>

        <h2 className="text-2xl font-semibold text-indigo-100 mb-4">1. Scope of Processing</h2>
        <p className="text-indigo-100 mb-6">
          We process personal data strictly for the purpose of delivering,
          maintaining, and improving our Services. Processing is carried out in
          accordance with user instructions, as described in the Terms of
          Service and Privacy Policy.
        </p>

        <h2 className="text-2xl font-semibold text-indigo-100 mb-4">2. Roles and Responsibilities</h2>
        <ul className="list-disc pl-6 text-indigo-100 mb-6 space-y-2">
          <li><strong>Controller:</strong> You, the user or customer, remain the controller of your data and are solely responsible for the lawfulness, accuracy, and appropriateness of any personal data you submit.</li>
          <li><strong>Processor:</strong> Andlegur acts as a processor on your behalf, processing data strictly in accordance with your instructions as set forth in the Terms of Service.</li>
          <li><strong>Sub-processors:</strong> We may engage vetted third-party service providers (e.g., hosting, analytics, payment processing) to support the Services. You acknowledge that we are not responsible or liable for the acts or omissions of sub-processors, and you agree to hold Andlegur harmless from any claims arising from sub-processor actions.</li>
        </ul>
        
        <div className="bg-indigo-950/40 border border-indigo-800/50 rounded-lg p-4 mb-6">
          <p className="text-indigo-100 text-sm">
            <strong className="text-indigo-50">User Responsibility:</strong> You are solely responsible for ensuring you have the legal right to share any personal data with Andlegur. You must not submit personal data of third parties without proper authorization and consent. Any violation is your responsibility, and you agree to indemnify Andlegur against any resulting claims.
          </p>
        </div>

        <h2 className="text-2xl font-semibold text-indigo-100 mb-4">3. International Transfers</h2>
        <p className="text-indigo-100 mb-6">
          If data is transferred outside your jurisdiction, we implement
          safeguards such as Standard Contractual Clauses (SCCs) or other lawful
          transfer mechanisms to ensure compliance.
        </p>

        <h2 className="text-2xl font-semibold text-indigo-100 mb-4">4. Security Measures</h2>
        <p className="text-indigo-100 mb-4">
          We implement industry-standard technical and organizational measures
          to protect personal data against unauthorized access, loss, misuse, or
          disclosure. However, no system is completely secure.
        </p>
        
        <div className="bg-indigo-950/40 border border-indigo-800/50 rounded-lg p-4 mb-6">
          <p className="text-indigo-100 text-sm">
            <strong className="text-indigo-50">Security Limitations:</strong> While we employ reasonable security measures, we cannot guarantee absolute security. You acknowledge and accept the inherent risks of transmitting data over the internet and storing data electronically. <strong>YOU ASSUME ALL RISKS</strong> associated with data security, and Andlegur shall not be liable for any unauthorized access, data breaches, or security incidents except where such liability cannot be excluded by law.
          </p>
        </div>

        <h2 className="text-2xl font-semibold text-indigo-100 mb-4">5. Data Subject Rights</h2>
        <p className="text-indigo-100 mb-4">
          Subject to applicable law, users may have the right to access, correct, delete, or restrict the use
          of their personal data. Requests can be submitted through the contact
          methods outlined in the Privacy Policy.
        </p>
        <p className="text-indigo-100 mb-6">
          <strong>Limitations:</strong> Rights are subject to legal limitations and exceptions. We reserve the right to verify your identity before processing requests. Some data may be retained for legal, security, or operational purposes even after a deletion request. Response times are subject to applicable legal requirements and our operational capacity.
        </p>

        <h2 className="text-2xl font-semibold text-indigo-100 mb-4">6. Duration of Processing</h2>
        <p className="text-indigo-100 mb-6">
          We retain and process personal data only for as long as necessary to
          provide our Services or as required by law. Retention periods are determined based on the nature of the data, the purposes for which it is processed, and legal obligations.
        </p>

        <h2 className="text-2xl font-semibold text-indigo-100 mb-4">7. Data Breach Notification</h2>
        <p className="text-indigo-100 mb-6">
          In the event of a data breach affecting personal data, we will notify affected users and relevant authorities as required by applicable law. Notification timelines and methods will comply with legal requirements. However, Andlegur shall not be liable for any damages, losses, or consequences arising from a data breach except where such liability cannot be excluded by law.
        </p>

        <h2 className="text-2xl font-semibold text-indigo-100 mb-4">8. Limitation of Liability</h2>
        <p className="text-indigo-100 mb-6">
          <strong className="text-indigo-50">TO THE MAXIMUM EXTENT PERMITTED BY LAW, ANDLEGUR'S LIABILITY UNDER THIS DPA IS LIMITED TO THE SAME EXTENT AS SET FORTH IN THE TERMS OF SERVICE.</strong> Andlegur shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from data processing activities, security incidents, sub-processor actions, or any other matter related to this DPA.
        </p>

        <h2 className="text-2xl font-semibold text-indigo-100 mb-4">9. User Indemnification</h2>
        <p className="text-indigo-100 mb-6">
          <strong className="text-indigo-50">YOU AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS ANDLEGUR FROM ANY CLAIMS, DAMAGES, LOSSES, LIABILITIES, OR EXPENSES (INCLUDING ATTORNEYS' FEES) ARISING FROM:</strong>
        </p>
        <ul className="list-disc pl-6 text-indigo-100 mb-6 space-y-2">
          <li>Your submission of personal data without proper authorization or consent</li>
          <li>Your violation of data protection laws or regulations</li>
          <li>Any inaccurate, unlawful, or inappropriate data you provide</li>
          <li>Claims by third parties whose data you submitted without authorization</li>
          <li>Your failure to comply with this DPA or applicable data protection laws</li>
        </ul>

        <p className="text-indigo-100 mb-8">
          This indemnification obligation survives termination of your account or the Services.
        </p>

        <div className="bg-indigo-950/40 border border-indigo-800/50 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-indigo-100 mb-3">Acknowledgment and Acceptance</h3>
          <p className="text-indigo-100 mb-3">
            By using the Services, you acknowledge that you have read and understood this DPA and agree to its terms. You further acknowledge that:
          </p>
          <ul className="list-disc pl-6 text-indigo-100 space-y-2">
            <li>You are solely responsible for the lawfulness of any data you provide</li>
            <li>Data processing carries inherent risks which you voluntarily assume</li>
            <li>This DPA does not create any additional obligations for Andlegur beyond those in the Terms of Service</li>
            <li>All refund policies, liability limitations, and indemnification obligations remain in full effect</li>
          </ul>
        </div>

        <p className="text-indigo-100">
          This summary is provided for convenience. For full details, please
          refer to the{" "}
          <a href="/termsofservice" className="text-indigo-300 font-semibold hover:underline">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="/privacy" className="text-indigo-300 font-semibold hover:underline">
            Privacy Policy
          </a>.
        </p>
      </div>
    </div>
  );
}
